import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentHeaderViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelContractCard`. */
export interface ParcelContractCardProps {
  /** Cabeçalho do contrato; `undefined` enquanto carrega ou em falha. */
  readonly header: IndividualPaymentHeaderViewModel | undefined;
  readonly isLoading: boolean;
  /** Rótulo da forma de pagamento do plano, já resolvido do id. */
  readonly paymentTypeLabel: string;
  /**
   * Exibe a tarja "Somente leitura" no cabeçalho da seção.
   *
   * Ligada na criação, onde o card é o único bloco não editável entre
   * formulários; desligada na edição, cuja tela já é toda de campos editáveis e
   * a tarja passou a ser ruído.
   */
  readonly showReadOnlyBadge?: boolean | undefined;
}

/**
 * Contexto do contrato na tela de criação de parcela, em leitura.
 *
 * Os dois primeiros campos são os que o legado exibe acima do formulário; os dois
 * últimos existem porque a decisão que o operador toma aqui depende deles: o
 * número da parcela se orienta pelo total contratado, e a forma de pagamento da
 * parcela é limitada pela do plano.
 *
 * A tarja de somente leitura é opcional (`showReadOnlyBadge`): na criação ela
 * distingue este bloco dos formulários ao redor; na edição foi retirada.
 */
export function ParcelContractCard({
  header,
  isLoading,
  paymentTypeLabel,
  showReadOnlyBadge,
}: ParcelContractCardProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <PlanSectionCard
      title={t('parcelCreate.contract.title')}
      icon={DocumentTextIcon}
      {...(showReadOnlyBadge === true ? { badge: t('parcelCreate.contract.badge') } : {})}
    >
      {isLoading ? (
        <div className="flex gap-xxxs">
          {[0, 1, 2, 3].map(index => (
            <div className="flex flex-1 flex-col gap-quark" key={index}>
              <Skeleton className="h-mini w-1/2" />
              <Skeleton className="h-xxs w-3/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-xxxs">
          <PlanReadOnlyField
            label={t('parcelCreate.contract.student')}
            value={header?.studentName ?? EMPTY_VALUE}
            hint={header?.email}
          />
          <PlanReadOnlyField
            label={t('parcelCreate.contract.course')}
            value={header?.courseName ?? EMPTY_VALUE}
          />
          <PlanReadOnlyField
            label={t('parcelCreate.contract.numberParcels')}
            value={header?.numberParcels != null ? String(header.numberParcels) : EMPTY_VALUE}
          />
          <PlanReadOnlyField
            label={t('parcelCreate.contract.paymentType')}
            value={paymentTypeLabel}
            hint={t('parcelCreate.contract.paymentTypeHint')}
          />
        </div>
      )}
    </PlanSectionCard>
  );
}
